<template>
	<VNavigationDrawer
		:model-value="modelValue"
		@update:model-value="$emit('update:modelValue', $event)"
		location="left"
		temporary
		scrim
		width="576"
		class="d-flex flex-column"
		style="height: 100%"
	>
		<div class="drawer-content d-flex flex-column" style="height: 100%">
			<!-- Header -->
			<div class="drawer-header">
				<VToolbar flat class="px-4 pt-4 pb-2">
					<!-- Left: Title and count -->
					<div class="d-flex align-center gap-2 flex-grow-1">
						<span class="text-h6 text-primary">
							{{ $t("message.DrawerTeachers.teachers") }}
						</span>
						<span
							v-if="teacherStore.selected.length"
							class="text-body-2 text-medium-emphasis"
						>
							{{
								$t("message.DrawerTeachers.selected", {
									count: teacherStore.selected.length,
								})
							}}
						</span>
					</div>

					<!-- Right: Button -->
					<VBtn
						variant="outlined"
						color="primary"
						class="ml-auto"
						@click="$emit('update:modelValue', false)"
						data-cy="event-teachers-close-btn"
						append-icon="mdi-arrow-right-bold"
					>
						{{ $t("message.DrawerTeachers.closeDrawer") }}
					</VBtn>
				</VToolbar>

				<VTextField
					v-model="teacherSearchInput"
					:label="$t('message.DrawerTeachers.search')"
					prepend-inner-icon="mdi-magnify"
					hide-details
					dense
					class="mb-4 px-4"
				/>
			</div>
            <!-- Scrollable teacher list -->
            <div class="scroll-area px-4">
				<VContainer fluid class="pa-0">
					<div v-if="teacherStore.loading" class="text-center">
						<loadingState />
					</div>
					<VRow>
                        <VCol
                            v-for="teacher in visibleTeachers"
							:key="teacher.uid"
							cols="12"
							:md="teacherResultsView === 'cards' ? 6 : 12"
						>
							<VCard
								:class="[
									'mb-2',
									teacher.checked ? 'selected-card' : '',
                                    (teacher._fetching || isDisabled(teacher)) ? 'v-card--disabled' : '',
								]"
								outlined
								@click="toggleTeacher(teacher)"
							>
								<VCardText class="pa-2">
									<div class="d-flex align-center justify-space-between">
										<strong>{{ teacher.name }}</strong>
									</div>
								
									<div v-if="isDisabled(teacher)" class="text-caption text-error">
										{{
											$t("message.DrawerTeachers.not_available", {
												date: dayjs(conferenceStore.selectedDay.start)
													.locale($i18n.locale)
													.format("MMM D"),
												})
										}}
									</div>

									<div v-if="teacher.description" class="mt-0">
										<VIcon icon="mdi-information-outline" class="mr-1" />
										{{ teacher.description }}
									</div>

									<div v-if="teacher.students?.length">
										<div v-for="student in teacher.students" :key="student.uid">
											<VIcon icon="mdi-emoticon-happy-outline" class="mr-1" />
											{{ student.child_fname }}
										</div>
									</div>
								</VCardText>
							</VCard>
						</VCol>
					</VRow>
                </VContainer>

                <!-- Pager controls -->
                <div class="d-flex align-center justify-center gap-2 my-3" v-if="teacherList.length">
                    <span class="text-body-2 mr-2">
                        {{ startItem }} - {{ endItem }} / {{ teacherList.length }}
                    </span>
                    <VBtn variant="text" :disabled="!hasPrev" @click="prevPage">Prev</VBtn>
                    <VBtn variant="text" :disabled="!hasNext" @click="nextPage">Next</VBtn>
                </div>

				<div
					class="text-center mt-4"
					v-if="conferenceStore.selected.show_more_teachers && !showMoreTeachersExecuted"
				>
					<VBtn
						@click="showMoreTeachers"
						color="primary"
						data-cy="show-more-teachers-btn"
					>
						{{ $t("message.DrawerTeachers.show_more") }}
						<VIcon end icon="mdi-chevron-down" />
					</VBtn>
				</div>
			</div>
			<!-- FOOTER -->
		</div>
	</VNavigationDrawer>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useTeacherStore } from "@/stores/parent/teacher";
import { useMatrixStore } from "@/stores/parent/matrix";
import { useConferenceStore } from "@/stores/parent/conference";
import { useAgendaStore } from "@/stores/parent/agenda";
import { useSchoolStore } from "@/stores/school";
import loadingState from "@/components/loadingState.vue";
import { complexSearch } from "@/utils";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

const { modelValue, conference } = defineProps({
	modelValue: Boolean,
	conference: Object,
});

defineEmits(["update:modelValue"]);

const teacherSearchInput = ref("");
const teacherResultsView = ref("cards");
const showMoreTeachersExecuted = ref(false);

const conferenceStore = useConferenceStore();
const teacherStore = useTeacherStore();
const matrixStore = useMatrixStore();
const agendaStore = useAgendaStore();
const schoolStore = useSchoolStore();
const router = useRouter();

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

function toggleSortDirection(key) {
	teacherStore.sort_asc = !teacherStore.sort_asc;
	teacherStore.sort_key = key;
}

function isDisabled(teacher) {
	return !teacher.conferencedays.find(
		(day) => day.conference_days_id === conferenceStore.selectedDay.conference_days_id
	);
}

const teacherList = computed(() => {
    if (teacherStore.loading) return [];

    const filterFn = (item) => {
        return complexSearch(
            teacherSearchInput.value,
            `${item.name || ""} ${item.description || ""} ${
                item.students
                    ?.map((s) => `${s.child_fname || ""} ${s.child_lname || ""}`)
                    .join(" ") || ""
            }`
        );
    };

    const list = teacherSearchInput.value
        ? [...teacherStore.teachers.filter(filterFn), ...teacherStore.teams.filter(filterFn)]
        : [...teacherStore.teachers, ...teacherStore.teams];

    return list.sort(teacherStore.sort);
});

// Button-based paging: render 30 at a time, replace window instead of accumulating
const PAGE_SIZE = 30;
const pageIndex = ref(0);
const visibleTeachers = computed(() =>
    teacherList.value.slice(pageIndex.value * PAGE_SIZE, pageIndex.value * PAGE_SIZE + PAGE_SIZE)
);
const totalCount = computed(() => teacherList.value.length);
const startItem = computed(() => (totalCount.value ? pageIndex.value * PAGE_SIZE + 1 : 0));
const endItem = computed(() => Math.min((pageIndex.value + 1) * PAGE_SIZE, totalCount.value));
const hasPrev = computed(() => pageIndex.value > 0);
const hasNext = computed(() => (pageIndex.value + 1) * PAGE_SIZE < totalCount.value);

function nextPage() {
    if (hasNext.value) pageIndex.value += 1;
}
function prevPage() {
    if (hasPrev.value) pageIndex.value -= 1;
}

// Reset to first page on search changes
watch(teacherSearchInput, () => {
    pageIndex.value = 0;
});
// Clamp page index if list shrinks
watch(totalCount, () => {
    if (pageIndex.value * PAGE_SIZE >= totalCount.value) {
        pageIndex.value = Math.max(0, Math.ceil(Math.max(totalCount.value - 1, 0) / PAGE_SIZE) - 1);
    }
});

function showMoreTeachers() {
	let conference_id = router.currentRoute.value.params.id;
	teacherStore.fetchAllTeachers(conference_id).then(() => {
		showMoreTeachersExecuted.value = true;
	});
}
function toggleTeacher(teacher) {
    if (teacher._fetching) return; // prevent toggling while fetching
	teacher.checked = !teacher.checked;
	selectTeacher(teacher); // this handles selection logic + timeslots
}
function selectTeacher(teacher) {
    // mark as in-flight to prevent immediate unclicks until fetch completes
    teacher._fetching = true;
	const index = teacherStore.getSelectedIndexById(teacher.uid);
	if (teacher.checked) {
		if (index < 0) {
			teacherStore.selected.push({ value: teacher });
			const timeInterval = teacher.conferencedays.find(
				(day) => day.conference_days_id === conferenceStore.selectedDay.conference_days_id
			)?.time_interval;

			if (
				timeInterval != null &&
				(matrixStore.smallestTimeInterval == null ||
					timeInterval < matrixStore.smallestTimeInterval)
			) {
				matrixStore.smallestTimeInterval = timeInterval;
			}
		}
	} else {
		if (index >= 0) {
			delete teacherStore.selected[index].value.conflicts;
			delete teacherStore.selected[index].value.timeslots;
			teacherStore.selected.splice(index, 1);
		}
		matrixStore.smallestTimeInterval = 15;
		teacherStore.selected.forEach((t) => {
			const interval = t.value.conferencedays.find(
				(day) => day.conference_days_id === conferenceStore.selectedDay.conference_days_id
			)?.time_interval;
			if (interval != null && interval < matrixStore.smallestTimeInterval) {
				matrixStore.smallestTimeInterval = interval;
			}
		});
	}

	fetchTimeslots(teacher);
}

async function fetchTimeslots(teacher) {
    try {
        teacher.conflicts = teacher.teacher_id
            ? await teacherStore.fetchTeacherConflict(conferenceStore.selectedDay, teacher)
            : await teacherStore.fetchTeamConflict(conferenceStore.selectedDay, teacher);

        const index = teacherStore.getSelectedIndexById(teacher.uid);
        if (index >= 0) {
            const timeInterval = teacherStore.selected[index].value.conferencedays.find(
                (day) => day.conference_days_id === conferenceStore.selectedDay.conference_days_id
            )?.time_interval;

            teacherStore.createTeacherTimeslots(
                teacherStore.selected[index].value,
                conferenceStore.selectedDay,
                timeInterval,
                agendaStore.agenda
            );
            teacherStore.clearDisabledTimeslots(teacherStore.selected[index].value);

            agendaStore.agenda
                .filter(
                    (meeting) =>
                        meeting.conference_days_id === conferenceStore.selectedDay.conference_days_id
                )
                .forEach((meeting) => {
                    teacherStore.disableTimeslots(
                        teacherStore.selected[index].value,
                        conferenceStore.selectedDay,
                        meeting
                    );
                });
        }
    } finally {
        teacher._fetching = false;
    }
}

function init() {
	const id = conference?.conference_id || router.currentRoute.value.params.id;
	// const id = props.conference?.conference_id || router.currentRoute.value.params.id;
	if (id) {
		teacherStore.fetchTeachers(id);
	}
}

function proceedToSchedule() {
	router.push({ name: "ScheduleView" });
}

onMounted(() => {
	init();
});
</script>

<style lang="scss" scoped>
@import "@/assets/_vars.scss";

.gap-2 {
	gap: 0.5rem;
}
.drawer-header {
	padding-top: 124px;
	flex: 0 0 auto;
	/* min-height: 100%; */
}
.v-toolbar {
	background-color: unset !important;
}
.scroll-area {
	overflow-y: auto;
	flex: 1 1 auto;
	min-height: 0;
	max-height: 100%;
}
.drawer-content {
	display: flex;
	flex-direction: column;
	height: 100%;
}
.selected-card {
	background-color: $secondary;
	color: white;
	cursor: pointer;
	transition: background-color 0.3s ease;
}
.v-card {
	cursor: pointer;
}
</style>
